import {
  getGlobalFilter,
  getProjectZoneId,
  getUsageGroupColumn,
  getUsageQueryType,
  getUsageSplitColumn,
} from '../reducers/analyticsApp';
import { getUsageEventName } from '../utils/usage';
import { toFilter } from './filterConverter';
import convertUniqueUsersResult from './revenueConverter';
import { getTypeField } from '../utils/event/createQueries';
import { toEventResult, toRange } from './eventConverter';
import { COLUMNS_CSV } from '../utils/csv';
import { toCsvEvent } from './csv/csvConverter';

const createUsageQuery = (state) => {
  const queryType = getUsageQueryType(state);
  const groupColumn = getUsageGroupColumn(state);
  const splitColumn = getUsageSplitColumn(state);
  const groups = splitColumn ? [groupColumn, splitColumn] : [groupColumn];
  return {
    eventName: getUsageEventName(queryType, groupColumn),
    groupBy: groups,
    aggregations: [
      {
        type: 'USERS',
        field: null,
      },
    ],
    globalFilter: toFilter(getGlobalFilter(state)),
    timezone: getProjectZoneId(state),
  };
};

const createSessionsQuery = (state) => ({
  eventName: 'SESSION_CLOSE',
  aggregations: [
    {
      type: 'COUNT',
      field: 'event.duration',
    },
    {
      type: 'TOTAL',
      field: 'event.duration',
    },
  ],
  globalFilter: toFilter(getGlobalFilter(state)),
  timezone: getProjectZoneId(state),
});

const createUniqueUsersQuery = (state) => ({
  eventName: 'DAU',
  aggregations: [
    {
      type: 'USERS',
      field: null,
    },
  ],
  globalFilter: toFilter(getGlobalFilter(state)),
  timezone: getProjectZoneId(state),
});

const mapUsageQueryResponse = (response, query, usageInfo) => {
  const { getType } = usageInfo;
  const result = toEventResult(response, query, '', getType);
  const options = toRange(result.info);

  return {
    events: [result],
    options,
  };
};

const mapSessionsQueryResponse = (response) => {
  const sessions = response.rows[0].count;
  const sessionsTotalLength = response.rows[0]['total.event.duration'];

  return {
    sessions,
    avgSessionLength: sessionsTotalLength / sessions,
  };
};

export const buildUsageQueries = (state) => {
  const usageQuery = createUsageQuery(state);
  const sessionsQuery = createSessionsQuery(state);
  const uniqueUsersQuery = createUniqueUsersQuery(state);
  return [usageQuery, sessionsQuery, uniqueUsersQuery];
};

export const getUsageInfo = (state) => {
  const getType = (field) => getTypeField(field, state);

  return { getType };
};

export const fromUsageResponse = (response, queries, usageInfo) => {
  const [usageQueryResponse, sessionsQueryResponse, uniqueUsersQueryResponse] = response;
  const [usageQuery] = queries;
  const { events, options } = mapUsageQueryResponse(usageQueryResponse, usageQuery, usageInfo);
  const { sessions, avgSessionLength } = mapSessionsQueryResponse(sessionsQueryResponse);
  const { uniqueUsers } = convertUniqueUsersResult(uniqueUsersQueryResponse);

  return { events, options, sessions, avgSessionLength, uniqueUsers };
};

export const buildCsvUsageQuery = (state) => {
  const queryType = getUsageQueryType(state);
  const groupColumn = getUsageGroupColumn(state);
  const splitColumn = getUsageSplitColumn(state);
  const groups = splitColumn ? [groupColumn, splitColumn] : [groupColumn];
  return {
    eventName: getUsageEventName(queryType, groupColumn),
    groupBy: groups,
    aggregations: [
      {
        type: 'USERS',
        field: null,
      },
    ],
    globalFilter: toFilter(getGlobalFilter(state)),
    timezone: getProjectZoneId(state),
  };
};

export const getCsvUsageInfo = (state) => {
  const getType = (field) => getTypeField(field, state);

  return { getType };
};

export const fromCsvUsageResponse = (response, query, csvUsageInfo) => {
  const { getType } = csvUsageInfo;
  const result = toEventResult(response, query, '', getType);
  const { info } = result;
  const columns = [info.groupBy, info.splitBy, COLUMNS_CSV.COUNT].filter((i) => !!i);
  return toCsvEvent(result, columns, getType);
};
